import * as React from 'react';
import { WorkspaceButton, WorkspaceDialog, WorkspaceNotice } from '@/components/ui/workspace';
import type { DraftView } from '@/lib/api/requirement-library';
import {
  pendingRename,
  plural,
  publishBlocker,
  publishChanges,
  unresolvedDuplicateCount,
} from './library-logic';

/**
 * "Review and publish" dialog (T-0141, E11-F02 scenarios 1, 7, 8, 12).
 *
 * Reference: `openPublish()` in `docs/requirement-library/prototype/
 * selection.js`. Lists what the draft changes, requires a change note,
 * warns when look-alike flags are still unresolved, and says why publish
 * is unavailable when there is nothing to publish.
 *
 * When the draft changed under the curator (`stale`), the list already
 * shows the current draft; the dialog says so and the curator publishes
 * again once they have read it.
 */

export interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleName: string;
  /** Version currently published; 0 for a module never published. */
  currentVersion: number;
  draft: DraftView | null | undefined;
  /** The draft was reloaded because someone else changed it. */
  stale: boolean;
  publishing: boolean;
  /** Server refusal other than a stale draft. */
  error: string | null;
  onPublish: (changeNote: string) => void;
}

export function PublishDialog({
  open,
  onOpenChange,
  moduleName,
  currentVersion,
  draft,
  stale,
  publishing,
  error,
  onPublish,
}: PublishDialogProps) {
  const [note, setNote] = React.useState('');
  React.useEffect(() => {
    if (open) setNote('');
  }, [open]);

  const next = currentVersion + 1;
  const changes = publishChanges(draft);
  const blocker = publishBlocker(draft);
  const openDuplicates = unresolvedDuplicateCount(draft);
  const renamedTo = pendingRename(draft);
  const canPublish = !blocker && note.trim().length > 0 && !publishing;

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Publish ${moduleName} v${next}`}
      description={`${
        currentVersion > 0
          ? `Version ${currentVersion} stays readable. `
          : 'First version of this module. '
      }Published versions cannot be edited.`}
      actions={
        <>
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Keep editing
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={!canPublish}
            onClick={() => onPublish(note.trim())}
          >
            {publishing ? 'Publishing…' : `Publish v${next}`}
          </WorkspaceButton>
        </>
      }
    >
      {stale ? (
        <WorkspaceNotice tone="at-risk">
          The draft changed while you were working: another curator edited it. The list below shows
          the draft as it is now. Read it, then publish.
        </WorkspaceNotice>
      ) : null}

      {changes.length > 0 ? (
        <ul
          aria-label="Changes in this draft"
          className="max-h-[200px] flex-none overflow-y-auto rounded-card border border-ink-100"
        >
          {changes.map((c) => (
            <li
              key={c.item_id}
              className="grid grid-cols-[72px_96px_1fr] gap-2.5 border-b border-ink-100 px-3 py-2 text-meta leading-[18px] last:border-b-0"
            >
              <span className="font-semibold text-ink-700">{c.kind}</span>
              <span className="truncate font-mono text-ink-700">
                {c.kind === 'Renamed' ? 'Module' : c.number}
              </span>
              <span className="truncate text-ink-700" title={c.text}>
                {c.text}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {blocker ? (
        <WorkspaceNotice tone="critical">{blocker}</WorkspaceNotice>
      ) : (
        <WorkspaceNotice>
          {currentVersion > 0
            ? `Engagements that already loaded ${moduleName} keep the wording they have. New loads get v${next}.`
            : `Once published, engagements can add ${moduleName} from their Requirements screen.`}
        </WorkspaceNotice>
      )}

      {renamedTo != null && !blocker ? (
        <WorkspaceNotice>
          This version renames the module to &ldquo;{renamedTo}&rdquo;. Its prefix and every
          requirement number stay the same, and engagements keep the name they loaded it under.
        </WorkspaceNotice>
      ) : null}

      {openDuplicates > 0 ? (
        <WorkspaceNotice tone="at-risk">
          {plural(openDuplicates, 'change')} in this draft {openDuplicates === 1 ? 'looks' : 'look'}{' '}
          like a requirement already in the library. You can still publish; close this to review{' '}
          {openDuplicates === 1 ? 'it' : 'them'} first.
        </WorkspaceNotice>
      ) : null}

      <div>
        <label
          htmlFor="rl-publish-note"
          className="mb-1.5 block text-meta font-medium text-ink-700"
        >
          Change note <span className="font-normal text-posture-critical">*</span>
        </label>
        <textarea
          id="rl-publish-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What changed and why, for the version history"
          required
          autoFocus
          maxLength={2000}
          className="block min-h-20 w-full resize-y rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body leading-5 text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600"
        />
      </div>

      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </WorkspaceDialog>
  );
}
